import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { toast, ToastContainer } from 'react-toastify';
import AdminSidebar from 'admin/sidebar/AdminSidebar';
import './SuperAdminPackages.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://production.washta.com';
const DUE_WASHES_URL = `${API_BASE}/api/residential/admin/due-washes`;
const ORDERS_URL = `${API_BASE}/api/residential/admin/orders`;

const PERIODS = ['Today', 'This week', 'This month', 'Custom'];

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessTokenAdmin')}`,
});

// ---- date helpers (moment, always normalized utc -> local) ----
const todayStr = () => moment.utc().local().format('YYYY-MM-DD');
const fmtDate = (d) => (d ? moment.utc(d).local().format('DD MMM YYYY') : '—');

const rangeForPeriod = (period) => {
  const now = moment.utc().local();

  if (period === 'Today') {
    const t = now.format('YYYY-MM-DD');
    return { startDate: t, endDate: t };
  }
  if (period === 'This week') {
    return {
      startDate: now.clone().startOf('isoWeek').format('YYYY-MM-DD'),
      endDate: now.clone().endOf('isoWeek').format('YYYY-MM-DD'),
    };
  }
  if (period === 'This month') {
    return {
      startDate: now.clone().startOf('month').format('YYYY-MM-DD'),
      endDate: now.clone().endOf('month').format('YYYY-MM-DD'),
    };
  }
  return null;
};

/* -------------------- Skeleton loading row -------------------- */
const SkeletonRow = () => (
  <tr className="sap-skel-row">
    {Array.from({ length: 10 }).map((_, i) => (
      <td key={i}>
        <div className="sap-skel-block" />
      </td>
    ))}
  </tr>
);

const DueWashes = () => {
  const [period, setPeriod] = useState('Today');
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());

  const [locationFilter, setLocationFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [query, setQuery] = useState('');

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ due: 0, completed: 0, pending: 0, missed: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const handlePeriodChange = (p) => {
    setPeriod(p);
    const range = rangeForPeriod(p);
    if (range) {
      setStartDate(range.startDate);
      setEndDate(range.endDate);
    }
  };

  const clearAll = () => {
    setPeriod('Today');
    setStartDate(todayStr());
    setEndDate(todayStr());
    setLocationFilter('');
    setProviderFilter('');
    setStatusFilter('');
    setTierFilter('');
    setQuery('');
  };

  const fetchDueWashes = async () => {
    try {
      setLoading(true);
      const params = { startDate, endDate };
      if (statusFilter) params.status = statusFilter;
      if (locationFilter) params.location = locationFilter;
      if (providerFilter) params.provider = providerFilter;
      if (tierFilter) params.tier = tierFilter;
      if (query.trim()) params.query = query.trim();
      const res = await axios.get(DUE_WASHES_URL, { headers: authHeader(), params });
      setRows(res?.data?.data?.rows || []);
      setStats(res?.data?.data?.stats || { due: 0, completed: 0, pending: 0, missed: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load due washes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDueWashes();
    // eslint-disable-next-line
  }, [startDate, endDate, statusFilter, locationFilter, providerFilter, tierFilter]);

  // debounce search so we don't hammer the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => fetchDueWashes(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [query]);

  // dropdown option lists, derived from whatever rows the API already returned
  const locationOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.locationDisplayName).filter(Boolean))),
    [rows]
  );
  const providerOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.sellerName).filter(Boolean))),
    [rows]
  );
  const tierOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.washCount).filter(Boolean))),
    [rows]
  );

  // client-side safety net filter (in case backend ignores some params)
  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (locationFilter && r.locationDisplayName !== locationFilter) return false;
      if (providerFilter && r.sellerName !== providerFilter) return false;
      if (tierFilter && String(r.washCount) !== String(tierFilter)) return false;
      if (
        q &&
        !(
          (r.plateNumber || '').toLowerCase().includes(q) ||
          (r.orderRef || '').toLowerCase().includes(q) ||
          (r.sellerName || '').toLowerCase().includes(q) ||
          (r.locationDisplayName || '').toLowerCase().includes(q)
        )
      )
        return false;
      return true;
    });
  }, [rows, locationFilter, providerFilter, tierFilter, query]);

  const adherence = useMemo(() => {
    const denom = stats.completed + stats.missed;
    if (!denom) return null;
    return ((stats.completed / denom) * 100).toFixed(1);
  }, [stats]);

  const runAction = async (row, action) => {
    setBusyId(row.washId);
    try {
      if (action === 'complete') {
        await axios.patch(`${ORDERS_URL}/${row.orderMongoId}/wash/${row.washId}/complete`, { reasonCode: 'superadmin_confirmed' }, { headers: authHeader() });
      } else if (action === 'reverse') {
        await axios.patch(`${ORDERS_URL}/${row.orderMongoId}/wash/${row.washId}/reverse`, { reasonCode: 'superadmin_reversed' }, { headers: authHeader() });
      }
      toast.success('Updated');
      fetchDueWashes();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const headerDateLabel =
    startDate === endDate ? fmtDate(startDate) : `${fmtDate(startDate)} → ${fmtDate(endDate)}`;

  return (
    <div className="mainDiv-all-sideBar">
      <div className="div-sidebar"><AdminSidebar /></div>
      <div className="div-Dashbaord" style={{ background: 'white', padding: 0 }}>
        <div className="width-settimg addtionwidth-setting sap-page">

          <div className="sap-title">Due Washes — {headerDateLabel}</div>
          <div className="sap-sub">A mirror of each provider's Daily Operations list across all providers and locations, with completion managed exclusively by the Super Admin. This feature is designed to audit provider adherence to required daily operations.</div>

          <div className="sap-stats">
            <div className="sap-stat"><div className="sap-stat-val">{stats.due}</div><div className="sap-stat-label">Due today</div></div>
            <div className="sap-stat"><div className="sap-stat-val green">{stats.completed}</div><div className="sap-stat-label">Completed</div></div>
            <div className="sap-stat"><div className="sap-stat-val amber">{stats.pending}</div><div className="sap-stat-label">Still pending</div></div>
            <div className="sap-stat"><div className="sap-stat-val red">{stats.missed}</div><div className="sap-stat-label">Missed</div></div>
            {adherence !== null && (
              <div className="sap-stat"><div className="sap-stat-val" style={{ fontSize: 18 }}>{adherence}%</div><div className="sap-stat-label">Adherence today</div></div>
            )}
          </div>

          {/* ---- Complete filter bar ---- */}
          <div className="sap-fbar">
            {/* Row 1 — period + date range */}
            <div className="sap-fbar-row">
              <span className="sap-flab">Period</span>
              <div className="sap-fseg">
                {PERIODS.map((p) => (
                  <div key={p} className={period === p ? 'on' : ''} onClick={() => handlePeriodChange(p)}>
                    {p}
                  </div>
                ))}
              </div>
              <input style={{width:'20%'}}
                type="date"
                className="sap-finput"
                value={startDate}
                disabled={period !== 'Custom'}
                onChange={(e) => { setPeriod('Custom'); setStartDate(e.target.value); }}
              />
              <span className="sap-flab" style={{ textTransform: 'none' }}>→</span>
              <input style={{width:'20%'}}
                type="date"
                className="sap-finput"
                value={endDate}
                disabled={period !== 'Custom'}
                onChange={(e) => { setPeriod('Custom'); setEndDate(e.target.value); }}
              />
              <span className="sap-fclear" onClick={clearAll}>Clear all</span>
            </div>

            {/* Row 2 — location / provider / status / tier */}
            <div className="sap-fbar-row">
              <span className="sap-flab">Filter</span>
              <select className="sap-fsel" value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                <option value="">All locations</option>
                {locationOptions.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              <select className="sap-fsel" value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
                <option value="">All providers</option>
                {providerOptions.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <select className="sap-fsel" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                <option value="scheduled">Pending</option>
                <option value="completed">Completed</option>
                <option value="missed">Missed</option>
              </select>
              <select className="sap-fsel" value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
                <option value="">All packages</option>
                {tierOptions.map((t) => (
                  <option key={t} value={t}>{t}/mo</option>
                ))}
              </select>
            </div>

            {/* Row 3 — search */}
            <div className="sap-fbar-row">
              <input
                className="sap-finput"
                style={{ flex: 1, minWidth: 220 }}
                placeholder="Search plate / order ref / provider / location"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <span className="sap-fclear" onClick={() => setQuery('')}>Clear search</span>
              )}
            </div>
          </div>

          {loading ? (
            <table className="sap-tbl">
              <thead>
                <tr>
                  <th>Plate</th><th>Car</th><th>Location</th><th>Provider</th><th>Order</th>
                  <th>Tier</th><th>Delivered</th><th>Ends</th><th>Status</th><th>Superadmin</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 7 }).map((_, i) => <SkeletonRow key={i} />)}
              </tbody>
            </table>
          ) : (
            <table className="sap-tbl">
              <thead>
                <tr>
                  <th>Plate</th><th>Car</th><th>Location</th><th>Provider</th><th>Order</th>
                  <th>Tier</th><th>Delivered</th><th>Ends</th><th>Status</th><th>Superadmin</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => (
                  <tr key={r.washId} className={r.status === 'missed' ? 'danger-row' : ''}>
                    <td><span className="sap-mono">{r.plateNumber}</span></td>
                    <td>{r.carType}</td>
                    <td>{r.locationDisplayName}</td>
                    <td>{r.sellerName || '—'}</td>
                    <td><span className="sap-mono">{r.orderRef}</span></td>
                    <td><span className="sap-pill acc">{r.washCount}/mo</span></td>
                    <td>{r.washesCompleted} / {r.washCount}</td>
                    <td className="sap-muted">{fmtDate(r.endDate)}</td>
                    <td>
                      {r.status === 'completed' && (
                        <span className={`sap-pill ${r.completedVia === 'superadmin' ? 'dark' : 'on'}`}>
                          ✓ {r.completedVia === 'superadmin' ? 'Superadmin' : 'Provider'}
                        </span>
                      )}
                      {r.status === 'scheduled' && <span className="sap-pill warn">Due</span>}
                      {r.status === 'missed' && <span className="sap-pill err">✕ Missed</span>}
                    </td>
                    <td>
                      {r.status !== 'completed' ? (
                        <button className="sap-btn sm" disabled={busyId === r.washId} onClick={() => runAction(r, 'complete')}>✓ Complete</button>
                      ) : (
                        <span className="sap-action-link" onClick={() => runAction(r, 'reverse')}>↩ Reverse</span>
                      )}
                    </td>
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr><td colSpan={10} className="sap-muted" style={{ textAlign: 'center', padding: 20 }}>There are no dues for this date range.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <ToastContainer position="bottom-right" />

      {/* Skeleton shimmer animation (scoped, no CSS file edit needed) */}
      <style>{`
        .sap-skel-block {
          height: 12px;
          border-radius: 4px;
          background: linear-gradient(90deg, #f0f0f4 25%, #e4e4ee 37%, #f0f0f4 63%);
          background-size: 400% 100%;
          animation: sapSkeletonShimmer 1.4s ease infinite;
        }
        @keyframes sapSkeletonShimmer {
          0% { background-position: 100% 50%; }
          100% { background-position: 0 50%; }
        }
      `}</style>
    </div>
  );
};

export default DueWashes;